// src/Blog.js

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../assets/img/ImageIndex";
import axios from "axios";
import BlogSidebar from "../components/BlogSidebar";
import BlogSkeleton from "../components/BlogSkeleton";

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://cspv0tmvph.execute-api.us-east-1.amazonaws.com/production';
      const response = await axios.get(`${API_BASE_URL}/blogs?page=${currentPage}&per_page=3`);
      setBlogs(response.data.data.blogs);
      // Calculate total pages based on your API response structure
      // You might need to adjust this based on your actual API response
      setTotalPages(Math.ceil(response.data.data.total / 3) || 1);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    // Set document title and meta tags
    document.title = 'Small Business Funding Insights & Loan Guides | EnvoCapital';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read expert insights on small business loans, working capital strategies, SBA guidance, and practical funding advice for business owners across the USA.');
    }

    fetchBlogs();
  }, [fetchBlogs]);

  const handleImageLoad = (blogId) => {
    setImageLoading(prev => ({ ...prev, [blogId]: false }));
  };

  const handleImageError = (blogId) => {
    setImageLoading(prev => ({ ...prev, [blogId]: false }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderBlogItem = (blog) => {
    const isImageLoading = imageLoading[blog.blog_id] !== false;

    return (
      <div key={blog.blog_id} className="col-lg-12 col-md-12 post-641 post type-post status-publish format-standard has-post-thumbnail hentry">
        <div className="blog-single-item transition-y mb-4">
          <ul className="tags d-flex ps-0 list-unstyled flex-wrap gap-2">
            <li><Link to="/blog/">Blog</Link></li>
          </ul>
          <h3>
            <Link to={`/blog-details/${blog.slug}`}>{blog.title}</Link>
          </h3>
          <Link to={`/blog-details/${blog.slug}`} className="blog-img d-block">
            {blog.image_path ? (
              <>
                {isImageLoading && (
                  <div className="skeleton-loader" style={{ height: '250px', backgroundColor: '#f0f0f0', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
                )}
                <img
                  src={blog.image_path}
                  alt={blog.title}
                  style={{ display: isImageLoading ? 'none' : 'block', width: '100%', height: '450px', objectFit: 'cover' }}
                  onLoad={() => handleImageLoad(blog.blog_id)}
                  onError={() => handleImageError(blog.blog_id)}
                />
              </>
            ) : (
              <img src={IMAGES.imageNotFound} alt={blog.title} style={{ width: '100%', height: '450px', objectFit: 'cover' }} />
            )}
          </Link>
          <p>{blog.short_description}</p>
          <ul className="ps-0 mb-0 list-unstyled d-flex flex-wrap blog-info">
            <li>
              <Link>
                <i className="ri-user-3-line"></i>
                <span>Admin</span>
              </Link>
            </li>
            <li>
              <Link>
                <i className="ri-calendar-2-line"></i>
                <span>{formatDate(blog.created_at)}</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    );
  };
  return (
    <div className="loan-program-page">
      <div className="page-banner-area bg-gray2">
        <div className="container mw-1380">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="page-banner-content">
                <ul className="ps-0 list-unstyled breadcrumbs">
                  <li><Link to={"/"}>Home</Link></li>
                  <li>&nbsp;Blog</li>
                </ul>
                <h1>Blog</h1>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="page-banner-img text-center text-lg-end position-relative z-1">
                <img src={IMAGES.shape7} className="shape shape-7" alt="Shape1" />
                <img src={IMAGES.shape8} className="shape shape-8" alt="Shape2" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="blog-page-area ptb-130">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              <div className="blog-left-sidebar">
                <div className="row">
                  {loading ? (
                    <>
                      <BlogSkeleton />
                      <BlogSkeleton />
                    </>
                  ) : blogs.length > 0 ? (
                    blogs.map(blog => renderBlogItem(blog))
                  ) : (
                    <div className="col-12">
                      <div className="text-center py-5">
                        <p>No blogs found.</p>
                      </div>
                    </div>
                  )}

                  {!loading && blogs.length > 0 && totalPages > 1 && (
                    <div className="pagination-area text-center">
                      <nav aria-label="navigation">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <Link
                            key={page}
                            aria-label={`Page ${page}`}
                            className={`page-numbers ${currentPage === page ? 'current' : ''}`}
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                          >
                            {page}
                          </Link>
                        ))}
                        {currentPage < totalPages && (
                          <Link
                            className="next page-numbers"
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(currentPage + 1);
                            }}
                          >
                            <i className="ri-arrow-right-line"></i>
                          </Link>
                        )}
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-12">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Blog;
